/**
 * StreamedArtifactSidePanel — right-hand side panel that renders an
 * artifact (markdown text). Modelled on ArtifactPanel's top bar so the
 * two sit in the same layout slot without visual dissonance.
 *
 * Top-bar actions (shown when the host wires the corresponding callback):
 *   • Edit / Save   — inline textarea editor. Host owns the PUT call.
 *   • Share         — publish + copy the demo URL. Host owns the flow.
 *   • Copy          — copy markdown to clipboard.
 *   • Download      — save as `.md` file.
 *   • Close         — hide the panel.
 *
 * Auto-opens when the assistant streams an artifact (see
 * ConversationPageTemplate). Clicking a past message's ArtifactPill
 * re-opens it with that message's artifact.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BlockNoteEditor } from '@blocknote/core';
import {
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  Download,
  Eye,
  FileText,
  Link2,
  Loader2,
  Maximize2,
  Minimize2,
  Pencil,
  Save,
  Share2,
  X,
} from 'lucide-react';
import type { MessageArtifact } from '@ideoz/core';
import { composeArtifactTitle, artifactService } from '@ideoz/core';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IdeozMessage,
  cn,
  showToast,
  isDownloadableFileFormat,
  getArtifactFileIcon,
  getArtifactFormatLabel,
} from '@ideoz/ui';
import BlockNoteArtifactEditor from './BlockNoteArtifactEditor';
import { genericArtifactMarkdown } from './genericArtifactMarkdown';
import { resolveArtifact } from '../renderers/registry';

export interface StreamedArtifactSidePanelProps {
  artifact: MessageArtifact | null;
  isOpen: boolean;
  onClose: () => void;
  /** Optional: hide/show the conversation column (mirrors ArtifactPanel). */
  onToggleConversationPanel?: () => void;
  isConversationPanelCollapsed?: boolean;
  /**
   * @deprecated Artifacts always render LTR now (they're structured documents,
   * not chat prose). Kept for call-site compatibility; no longer read.
   */
  isRtl?: boolean;
  className?: string;
  /**
   * Persist edited markdown (legacy markdown/BlockNote artifacts). Host owns the
   * PUT + reflecting the new content back. Panel returns to view mode on success.
   */
  onSave?: (content: string) => Promise<void>;
  /**
   * Persist an edited structured (JSON) artifact — the host PUTs the JSON to
   * update the artifact in place. Panel returns to view mode on success. Used
   * for the typed cards (JTBD, persona, 5 Whys, HMW).
   */
  onSaveStructured?: (json: Record<string, unknown>) => Promise<void>;
  /**
   * Trigger the share flow (publish demo_url + copy to clipboard).
   * Host handles the network + toast + updates the artifact.demoUrl prop.
   */
  onShare?: () => Promise<void>;
  /**
   * Open artifacts as tabs. When two or more are passed, a tab strip renders
   * above the top bar; `artifact` must be the active tab's artifact. Omitted
   * (or a single tab) → no strip, classic single-artifact panel.
   */
  tabs?: Array<{ key: string; artifact: MessageArtifact }>;
  activeTabKey?: string | null;
  onSelectTab?: (key: string) => void;
  onCloseTab?: (key: string) => void;
}

const humanise = (raw: string): string => {
  const trimmed = raw.replace(/[_-]+/g, ' ').trim();
  if (!trimmed) return raw;
  return trimmed
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Pick a display title. Prefers the artifact's first markdown heading —
 * the AI writes the "real" name there (e.g. "# Renovation Permit
 * Issuance: End-to-End Service Blueprint") — so a row saved with a
 * generic `artifact_type` still shows a meaningful title. Falls back to
 * the humanised name when there's no heading.
 */
const resolveTitle = (artifact: { name: string; content: string | null | undefined }): string => {
  const content = artifact.content ?? '';
  const match = content.match(/^\s*#{1,6}\s+(.+?)\s*$/m);
  if (match?.[1]) return match[1].trim();
  return humanise(artifact.name || 'Artifact');
};

const artifactExtension = (format: string | null | undefined): string => {
  switch ((format ?? 'markdown').toLowerCase()) {
    case 'markdown':
      return 'md';
    case 'html':
      return 'html';
    default:
      return 'txt';
  }
};

/**
 * A file-backed artifact (PDF and other non-markdown formats) has a real
 * `fileFormat` and a persisted `id`, so its binary must be fetched from
 * `/artifacts/:id/download` rather than saving the markdown `content`. The
 * format check is shared with the pill via `isDownloadableFileFormat`.
 */
const isDownloadableFile = (
  artifact: Pick<MessageArtifact, 'id' | 'fileFormat'>
): boolean => Boolean(artifact.id && isDownloadableFileFormat(artifact.fileFormat));

const slugify = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60) || 'artifact';

export const StreamedArtifactSidePanel: React.FC<StreamedArtifactSidePanelProps> = ({
  artifact,
  isOpen,
  onClose,
  onToggleConversationPanel,
  isConversationPanelCollapsed,
  className,
  onSave,
  onSaveStructured,
  onShare,
  tabs,
  activeTabKey,
  onSelectTab,
  onCloseTab,
}) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  // Edit-mode state. BlockNote owns the document; we just hold a ref to
  // the live editor so `handleSave` can call `blocksToMarkdownLossy` on
  // the current doc without re-rendering the panel on every keystroke.
  const [isEditing, setIsEditing] = React.useState(false);
  const [editSeed, setEditSeed] = React.useState<string>('');
  const editorRef = React.useRef<BlockNoteEditor | null>(null);
  // Structured artifacts (persona/JTBD/5 Whys/HMW) edit via a typed form
  // instead of BlockNote; this holds the form's serialiser (set on the editor's
  // onReady) so Save can read canonical markdown from it.
  const structuredSaveRef = React.useRef<(() => Record<string, unknown>) | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isSharing, setIsSharing] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  // Inline PDF preview: PDFs are rendered natively in an <iframe> from a blob
  // URL. The blob comes from the same download endpoint the Office trio uses;
  // we just embed it instead of forcing a download. Office formats have no
  // in-browser renderer, so they show a file card (Download) instead.
  const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
  const [pdfError, setPdfError] = React.useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);
  // Detect dark mode from a document-level `.dark` class the app already
  // toggles for the theme. Keeps the BlockNote view in sync without
  // adding a new context.
  const [isDark, setIsDark] = React.useState<boolean>(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setIsEditing(false);
    }
  }, [isOpen, artifact?.content]);

  // Fetch the PDF binary and expose it as a blob URL for the inline <iframe>.
  // Recomputes the "is this a previewable PDF" check here (the derived vars live
  // after the bail-out below, but hooks must run every render).
  React.useEffect(() => {
    const fmt = (artifact?.fileFormat ?? '').toLowerCase();
    const isPdfPreview =
      isOpen && !!artifact?.id && fmt === 'pdf' && isDownloadableFileFormat(artifact?.fileFormat);
    if (!isPdfPreview) {
      setPdfUrl(null);
      setPdfError(false);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setPdfError(false);
    setIsLoadingPreview(true);
    (async () => {
      try {
        const fallbackName = artifact!.fileName || `${slugify(artifact!.name)}.pdf`;
        const { blob } = await artifactService.downloadArtifactFile(artifact!.id!, fallbackName);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch {
        if (!cancelled) setPdfError(true);
      } finally {
        if (!cancelled) setIsLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, artifact?.id, artifact?.fileFormat, artifact?.fileName, artifact?.name]);

  // Bail out AFTER hooks so the hook order stays stable across renders.
  if (!isOpen || !artifact) return null;

  // Prefer the typed title block ("artifact_name - artifact_type - version") from
  // the structured JSON; fall back to the first markdown heading / humanised name.
  const title =
    composeArtifactTitle(artifact.structuredContent) ??
    resolveTitle({ name: artifact.name, content: artifact.content });
  // Title for a tab strip entry (same resolution as the header title).
  const tabTitle = (a: MessageArtifact): string =>
    composeArtifactTitle(a.structuredContent) ?? resolveTitle({ name: a.name, content: a.content });
  const showTabs = !!tabs && tabs.length >= 2;
  // Typed cards (JTBD, persona, 5 Whys, HMW, Atlas) render from structuredContent.
  // A user story also has structuredContent (a title plus the markdown body) but
  // no card. History marks that shape `format: "json"`, so the body has to be
  // read from the markdown string and rendered, not printed in a <pre>.
  const view = artifact.structuredContent
    ? resolveArtifact(artifact.structuredContent, artifact.name)
    : null;
  const markdownBody = view ? null : genericArtifactMarkdown(artifact);
  // File-backed artifacts (PDF / DOCX / XLSX / PPTX): a real binary from the
  // download endpoint, not markdown. PDFs preview inline; the Office trio shows
  // a file card. Either way they're not editable as text.
  const isFileArtifact = isDownloadableFile(artifact);
  const fileFormat = (artifact.fileFormat ?? '').toLowerCase();
  const isPdf = isFileArtifact && fileFormat === 'pdf';
  // The file row is written by an async task, so a just-generated artifact can
  // arrive before its binary exists (`fileStatus` not ready / no `id` yet).
  const fileReady = Boolean(artifact.id) && artifact.fileStatus !== 'pending';
  // Show Edit / Share whenever the host wired the matching handler. We no longer
  // also require `artifact.id` up front: the id can lag behind (the DB row is
  // written by an async task, so a just-generated artifact briefly has none), and
  // gating on it made the buttons vanish "sometimes". The handlers resolve the id
  // themselves (see useStreamedArtifactPanel), so keep the controls visible.
  // File-backed artifacts can't be edited as markdown — hide Edit for them.
  const canEdit = (view ? !!onSaveStructured : !!onSave) && !isFileArtifact;
  const canShare = !!onShare;

  const beginEdit = () => {
    // Seed the editor with the current markdown once — BlockNote takes
    // over from there. `editSeed` never changes for the duration of an
    // edit session, so the editor mount effect only runs once.
    setEditSeed(markdownBody ?? artifact.content ?? '');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditSeed('');
    editorRef.current = null;
    structuredSaveRef.current = null;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (view && structuredSaveRef.current && onSaveStructured) {
        // Structured artifact → serialise the typed form to JSON and POST it
        // (saved as a new artifact).
        await onSaveStructured(structuredSaveRef.current());
      } else if (onSave && editorRef.current) {
        // Legacy markdown → BlockNote → markdown PUT.
        const markdown = (
          await editorRef.current.blocksToMarkdownLossy(editorRef.current.document)
        ).trim();
        await onSave(markdown);
      } else {
        return;
      }
      setIsEditing(false);
      setEditSeed('');
      editorRef.current = null;
      structuredSaveRef.current = null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleShare = async () => {
    if (!onShare) return;
    setIsSharing(true);
    try {
      await onShare();
    } finally {
      setIsSharing(false);
    }
  };

  // Once shared, the artifact has a public URL — re-copy it instead of
  // re-publishing.
  const handleCopyLink = async () => {
    if (!artifact.demoUrl) return;
    try {
      await navigator.clipboard.writeText(artifact.demoUrl);
      showToast({ message: t('artifacts.linkCopied'), severity: 'success' });
    } catch {
      showToast({ message: t('artifacts.linkCopyFailed'), severity: 'error' });
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdownBody ?? artifact.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable — fail silently, user can select text.
    }
  };

  const triggerBrowserDownload = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const saveBlob = (contents: string, ext: string, mime: string) =>
    triggerBrowserDownload(new Blob([contents], { type: mime }), `${slugify(artifact.name)}.${ext}`);

  // File-backed artifacts (PDF, etc.): fetch the real binary from the server —
  // the markdown `content` is only a preview rendition, not the actual file.
  const downloadFile = async () => {
    if (!artifact.id || isDownloading) return;
    setIsDownloading(true);
    try {
      const fallbackName =
        artifact.fileName ||
        `${slugify(artifact.name)}.${(artifact.fileFormat || 'bin').toLowerCase()}`;
      const { blob, fileName } = await artifactService.downloadArtifactFile(
        artifact.id,
        fallbackName
      );
      triggerBrowserDownload(blob, fileName);
    } catch {
      showToast({ message: t('artifacts.downloadFailed'), severity: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  // Structured artifacts download as the visualised HTML card or Markdown
  // (both derived from the JSON model); every other artifact downloads its raw
  // markdown/text.
  const downloadHtml = () =>
    view && saveBlob(view.renderer.toHtml(view.model, title), 'html', 'text/html;charset=utf-8');
  const downloadMarkdown = () =>
    view
      ? saveBlob(view.renderer.toMarkdown(view.model), 'md', 'text/markdown;charset=utf-8')
      : saveBlob(
          markdownBody ?? artifact.content,
          markdownBody != null ? 'md' : artifactExtension(artifact.format),
          markdownBody != null ? 'text/markdown;charset=utf-8' : 'text/plain;charset=utf-8'
        );

  return (
    <div
      className={cn('h-full flex flex-col bg-white dark:bg-gray-900', className)}
      data-testid="streamed-artifact-side-panel"
    >
      {/* Top bar — tab chips (when 2+ artifacts are open) or the single title,
          plus the actions on the right. Same shape as ArtifactPanel. */}
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0 h-16">
        {showTabs ? (
          <div
            className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto scrollbar-chat"
            role="tablist"
            data-testid="streamed-artifact-tabs"
          >
            {tabs!.map((tab) => {
              const active = tab.key === activeTabKey;
              return (
                <div
                  key={tab.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => onSelectTab?.(tab.key)}
                  className={cn(
                    'group flex max-w-[200px] shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition-colors',
                    active
                      ? 'border-gray-200 bg-gray-50 font-semibold text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
                      : 'border-transparent text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  )}
                  data-testid={`streamed-artifact-tab-${tab.key}`}
                >
                  <FileText size={13} className="shrink-0 text-primary-500" />
                  <span className="truncate">{tabTitle(tab.artifact)}</span>
                  <button
                    type="button"
                    aria-label={t('artifacts.closeTab', { defaultValue: 'Close tab' })}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseTab?.(tab.key);
                    }}
                    className={cn(
                      'ms-0.5 shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700',
                      active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    )}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-primary-500 shrink-0" />
            <div className="min-w-0">
              <h2
                className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                data-testid="streamed-artifact-title"
              >
                {title}
              </h2>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 shrink-0">
          {onToggleConversationPanel && (
            // <Button
            //   onClick={onToggleConversationPanel}
            //   variant="ghost"
            //   size="sm"
            //   className="!p-1 h-auto bg-gray-100 hover:bg-gray-100 gap-0.5"
            //   aria-label={
            //     isConversationPanelCollapsed ? 'Show conversation panel' : 'Hide conversation panel'
            //   }
            //   type="button"
            // >
            //   <Icon
            //     icon={isConversationPanelCollapsed ? ChevronsRight : ChevronsLeft}
            //     size={22}
            //     className={cn(
            //       'rounded-sm p-[2px]',
            //       isConversationPanelCollapsed
            //         ? 'bg-white stroke-primary-600'
            //         : 'stroke-primary-900'
            //     )}
            //   />
            //   <Icon
            //     icon={Eye}
            //     size={22}
            //     className={cn(
            //       'rounded-sm p-[2px]',
            //       isConversationPanelCollapsed
            //         ? 'stroke-primary-900'
            //         : 'bg-white stroke-primary-600'
            //     )}
            //   />
            // </Button>
            <Button variant="outline" size="xs" onClick={onToggleConversationPanel}>
              <Icon icon={isConversationPanelCollapsed ? Minimize2 : Maximize2} size={16} />
            </Button>
          )}
          {canShare &&
            !isEditing &&
            (artifact.demoUrl ? (
              // Already shared → copy the existing link instead of re-publishing.
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={handleCopyLink}
                className="gap-1.5"
                data-testid="streamed-artifact-copy-link"
              >
                <Link2 size={12} />
                {t('artifacts.copyLink')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={handleShare}
                className="gap-1.5"
                disabled={isSharing}
                data-testid="streamed-artifact-share"
              >
                <Share2 size={12} />
                {isSharing ? t('artifacts.sharing') : t('artifacts.share')}
              </Button>
            ))}
          {canEdit && !isEditing && (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={beginEdit}
              className="gap-1.5"
              data-testid="streamed-artifact-edit"
            >
              <Pencil size={12} />
              {t('artifacts.edit')}
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={cancelEdit}
                disabled={isSaving}
                data-testid="streamed-artifact-cancel"
              >
                {t('artifacts.cancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="xs"
                onClick={handleSave}
                disabled={isSaving}
                className="gap-1.5"
                data-testid="streamed-artifact-save"
              >
                <Save size={12} />
                {isSaving ? t('artifacts.saving') : t('artifacts.save')}
              </Button>
            </>
          )}
          {!isEditing && (
            <>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={handleCopy}
                className="gap-1.5"
                data-testid="streamed-artifact-copy"
              >
                <Copy size={12} />
                {copied ? t('artifacts.copied') : t('artifacts.copy')}
              </Button>
              {view ? (
                // Structured artifacts offer a format choice: the visualised
                // HTML card or the raw markdown.
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      className="gap-1.5"
                      data-testid="streamed-artifact-download"
                    >
                      <Download size={12} />
                      {t('artifacts.download')}
                      <ChevronDown size={12} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={downloadHtml}
                      data-testid="streamed-artifact-download-html"
                    >
                      {t('artifacts.downloadHtml')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={downloadMarkdown}
                      data-testid="streamed-artifact-download-md"
                    >
                      {t('artifacts.downloadMd')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={isFileArtifact ? downloadFile : downloadMarkdown}
                  disabled={isFileArtifact && isDownloading}
                  className="gap-1.5"
                  data-testid="streamed-artifact-download"
                >
                  <Download size={12} />
                  {isFileArtifact && isDownloading
                    ? t('artifacts.downloading')
                    : t('artifacts.download')}
                </Button>
              )}
            </>
          )}
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onClose}
            className="!p-1 h-auto"
            aria-label={t('artifacts.close')}
            data-testid="streamed-artifact-close"
          >
            <X size={16} />
          </Button>
        </div>
      </div>

      {/* Body — markdown/structured card. Follows the app language direction
          (RTL in Arabic); individual values still resolve their own direction
          from content (see `dirOf`), so mixed-language values stay correct. */}
      <div
        dir={i18n.dir()}
        className="flex-1 min-h-0 overflow-auto bg-gray-50 dark:bg-gray-800 p-6"
        data-testid="streamed-artifact-body"
      >
        {isEditing && view ? (
          // Structured typed form — serialises back to JSON on Save (PUT updates
          // the artifact in place). Keeps the strict field shape intact (unlike
          // a lossy markdown editor).
          <view.renderer.Editor
            model={view.model}
            onReady={(getJson) => {
              structuredSaveRef.current = getJson;
            }}
          />
        ) : isEditing ? (
          <div className="px-6 py-5 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            {/* Block-style WYSIWYG editor (BlockNote). Seeded from the
                artifact's current markdown once; typing lives inside
                BlockNote until Save re-serialises to markdown. */}
            <BlockNoteArtifactEditor
              initialMarkdown={editSeed}
              onReady={(editor) => {
                editorRef.current = editor;
              }}
              isRtl={false}
              isDark={isDark}
            />
          </div>
        ) : view ? (
          // Structured artifacts get a bespoke designed card mapped from their
          // JSON (persona / JTBD / 5 Whys / HMW).
          <view.renderer.Card model={view.model} />
        ) : isFileArtifact ? (
          // File-backed artifact (PDF / DOCX / XLSX / PPTX).
          //  • PDF: rendered inline in an <iframe> (native).
          //  • Office trio: show the markdown rendition the model produced in a
          //    Notion-like view (same as a generic artifact) — the real file is
          //    still one tap away via the top-bar Download (fetches the binary).
          //  • No markdown and no inline renderer → a download file card.
          !fileReady ? (
            <ArtifactFilePreparing t={t} />
          ) : isPdf && pdfUrl && !pdfError ? (
            <iframe
              src={pdfUrl}
              title={title}
              className="h-full w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700"
            />
          ) : isPdf && !pdfError ? (
            <ArtifactFilePreparing t={t} loading />
          ) : markdownBody != null || artifact.content ? (
            <div className="px-6 py-5 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <IdeozMessage hideAvatar>{markdownBody ?? artifact.content}</IdeozMessage>
            </div>
          ) : (
            <ArtifactFileCard
              format={artifact.fileFormat}
              fileName={artifact.fileName || `${slugify(artifact.name)}.${fileFormat || 'bin'}`}
              onDownload={downloadFile}
              isDownloading={isDownloading}
              note={
                pdfError
                  ? t('artifacts.filePreviewFailed', {
                      defaultValue: 'Preview unavailable — download the file to view it.',
                    })
                  : undefined
              }
              t={t}
            />
          )
        ) : (
          <div className="px-6 py-5 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            {markdownBody != null ? (
              // Omit isRtl so IdeozMessage auto-detects direction from the content:
              // an Arabic document renders RTL/right-aligned, English stays LTR.
              <IdeozMessage hideAvatar>{markdownBody}</IdeozMessage>
            ) : (
              <pre className="whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-100">
                {artifact.content}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/** Centered "preparing / loading" state for a file-backed artifact. */
function ArtifactFilePreparing({
  t,
  loading = false,
}: {
  t: (key: string, opts?: Record<string, unknown>) => string;
  loading?: boolean;
}): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-gray-500 dark:text-gray-400">
      <Loader2 className="size-6 animate-spin text-primary-500" />
      <p className="text-sm">
        {loading
          ? t('artifacts.filePreviewLoading', { defaultValue: 'Loading preview…' })
          : t('artifacts.filePreparing', { defaultValue: 'Preparing your file…' })}
      </p>
    </div>
  );
}

/**
 * File card for a file-backed artifact with no in-browser preview (DOCX / XLSX /
 * PPTX, or a PDF whose preview failed): icon + name + format badge + download.
 */
function ArtifactFileCard({
  format,
  fileName,
  onDownload,
  isDownloading,
  note,
  t,
}: {
  format?: string | null;
  fileName: string;
  onDownload: () => void;
  isDownloading: boolean;
  note?: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}): JSX.Element {
  const FileIcon = getArtifactFileIcon(format);
  const formatLabel = getArtifactFormatLabel(format);
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <span className="flex size-14 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
          <Icon icon={FileIcon} size={28} />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100" title={fileName}>
            {fileName}
          </p>
          {formatLabel && (
            <span className="mt-1 inline-block rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              {formatLabel}
            </span>
          )}
        </div>
        <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
          {note ??
            t('artifacts.fileNoPreview', {
              defaultValue: "This file type can't be previewed here — download it to open.",
            })}
        </p>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={onDownload}
          disabled={isDownloading}
          className="gap-1.5"
        >
          <Download size={14} />
          {isDownloading
            ? t('artifacts.downloading')
            : t('artifacts.download')}
        </Button>
      </div>
    </div>
  );
}

export default StreamedArtifactSidePanel;
